import { useState, useEffect } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import styled from "styled-components"
import Navbar from "./Navbar"
import ProfileDropdown from "./ProfileDropdown"
import { useTheme } from "../context/ThemeContext"
import { useAuth } from "../context/AuthContext"
import { MessageCircle, X, Send, MessageSquare, Bell, Trophy, Heart, Search } from "lucide-react"

const Container = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 100vh;
  background-color: ${props => props.theme.backgroundColor};
  transition: background-color 0.3s ease;
`

const TopHeader = styled.header`
  position: fixed;
  top: 0;
  right: 0;
  left: 240px;
  height: 64px;
  background-color: ${props => props.theme.cardBackground};
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 24px;
  z-index: 99;

  @media (max-width: 768px) {
    left: 200px;
    padding: 0 16px;
  }

  @media (max-width: 650px) {
    left: 0;
    padding: 0 12px;
  }
`

const HeaderSearch = styled.div`
  flex: 1;
  max-width: 720px;
  position: relative;

  svg.lead {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.textMuted};
    pointer-events: none;
  }

  input {
    width: 100%;
    padding: 11px 48px 11px 42px;
    border: 1px solid ${props => props.theme.border};
    border-radius: 12px;
    background: ${props => props.theme.hoverBackground};
    font-size: 13.5px;
    color: ${props => props.theme.textPrimary};
    transition: all 0.15s;

    &::placeholder { color: ${props => props.theme.textMuted}; }
    &:focus {
      outline: none;
      border-color: #7c3aed;
      box-shadow: 0 0 0 3px rgba(124,58,237,0.10);
      background: ${props => props.theme.cardBackground};
    }
  }

  .shortcut {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: ${props => props.theme.textMuted};
    background: ${props => props.theme.cardBackground};
    border: 1px solid ${props => props.theme.border};
    border-radius: 6px;
    padding: 2px 8px;
    font-weight: 600;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    .shortcut { display: none; }
  }
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const WelcomeText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  white-space: nowrap;
  margin-right: 4px;

  .name {
    color: #7c3aed;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const ProfileButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.theme.buttonSecondary};
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;

  &:hover {
    transform: scale(1.05);
    box-shadow: ${props => props.theme.shadowHover};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const HeaderIconButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background-color: transparent;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    color: ${props => props.theme.textPrimary};
  }
`

const HeaderNotificationBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  background-color: #ef4444;
  color: white;
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 999px;
  min-width: 16px;
  text-align: center;
  border: 1.5px solid ${props => props.theme.cardBackground};
`

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 240px;
  margin-top: 0;

  @media (max-width: 768px) {
    margin-left: 200px;
  }

  @media (max-width: 650px) {
    margin-left: 0;
  }
`

const Content = styled.div`
  display: flex;
  flex: 1;
  padding: 0;
  gap: 20px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0;
    gap: 15px;

    /* 홈 페이지에서만 사이드바를 메인 콘텐츠 아래로 */
    &.home-layout {
      .mobile-sidebar {
        order: 2;
      }

      .mobile-main {
        order: 1;
      }
    }
  }

  @media (max-width: 480px) {
    padding: 0;
    gap: 12px;
  }

  @media (max-width: 350px) {
    padding: 0;
    gap: 10px;
  }
`

const MainContent = styled.main`

  flex: 1;
`

const Sidebar = styled.aside`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    width: 100%;
  }
`


const DesktopLoginSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 768px) {
    display: none;
  }
`

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 768px) {
    margin-top: 20px;
  }
`

const MobileLoginPanel = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 400px;
    background-color: ${props => props.theme.cardBackground};
    border-radius: 12px;
    box-shadow: ${props => props.theme.shadowHover};
    z-index: 1100;
    animation: fadeIn 0.2s ease-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`

const MobileLoginBackdrop = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1099;
  }
`

const ToggleButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${props => props.theme.shadowHover};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
  }
  
  @media (max-width: 350px) {
    width: 46px;
    height: 46px;
  }
`

const MobileMessagePanel = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background-color: ${props => props.theme.cardBackground};
    z-index: 9999;
    animation: ${props => props.$isOpen ? 'slideInFromRight' : 'slideOutToRight'} 0.3s ease-out;
    overflow: hidden;
  }
  
  @media (min-width: 769px) {
    display: none !important;
  }
  
  @keyframes slideInFromRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOutToRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`

const MessagePanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme.border};
  min-height: 60px;
  background-color: ${props => props.theme.cardBackground};
  
  @media (max-width: 480px) {
    padding: 14px 16px;
    min-height: 56px;
  }
  
  @media (max-width: 350px) {
    padding: 12px 14px;
    min-height: 52px;
  }
`

const MessagePanelTitle = styled.h3`
  margin: 0;
  color: ${props => props.theme.textPrimary};
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 480px) {
    font-size: 16px;
    gap: 6px;
  }
  
  @media (max-width: 350px) {
    font-size: 15px;
    gap: 5px;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    color: ${props => props.theme.textPrimary};
    background-color: ${props => props.theme.hoverBackground};
  }
`

const ConversationsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`

const ConversationItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  @media (max-width: 480px) {
    padding: 10px 16px;
  }
  
  @media (max-width: 350px) {
    padding: 8px 14px;
  }
`

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.border};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
`

const ConversationInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const ConversationName = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LastMessage = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EmptyMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
  
  .icon {
    margin-bottom: 12px;
    color: ${props => props.theme.textMuted};
  }
  
  h4 {
    margin: 0 0 8px 0;
    color: ${props => props.theme.textPrimary};
    font-size: 16px;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.4;
  }
`

const ViewAllButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  background: none;
  border: none;
  border-top: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.buttonPrimary};
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const NotificationPanel = styled.div`
  position: fixed;
  top: 70px;
  right: 70px; /* 알림 IconBtn 아래로 정렬 (프로필 영역 폭만큼 우측 비움) */
  width: 380px;
  max-height: calc(100vh - 90px);
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0,0,0,0.08);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  z-index: 1000;
  overflow: hidden;

  @media (max-width: 768px) {
    right: 60px;
    width: 340px;
  }

  @media (max-width: 650px) {
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-height: calc(100vh - 64px);
    border-radius: 0;
    border: none;
  }
`

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.cardBackground};
`

const NotificationTitle = styled.h3`
  margin: 0;
  color: ${props => props.theme.textPrimary};
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`

const NotificationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`

const NotificationItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.$isRead ? 'transparent' : '#f3e8ff'};
  opacity: ${props => props.$isRead ? 0.6 : 1};

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    opacity: 1;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #e5e5e5;
  }
`

const NotificationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${props => {
    if (props.$type === 'play_milestone') return props.theme.isDarkMode ? props.theme.borderLight : '#fef3c7';
    if (props.$type === 'like' || props.$type === 'post_like' || props.$type === 'comment_like')
      return props.theme.isDarkMode ? props.theme.borderLight : '#fecaca';
    return props.theme.borderLight;
  }};
  color: ${props => {
    if (props.$type === 'play_milestone') return '#f59e0b';
    if (props.$type === 'like' || props.$type === 'post_like' || props.$type === 'comment_like')
      return props.theme.error;
    return props.theme.textSecondary;
  }};
`

const NotificationContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const NotificationItemTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  font-size: 15px;
  margin-bottom: 4px;
`

const NotificationItemMessage = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  margin-bottom: 6px;
  line-height: 1.4;
`

const NotificationItemTime = styled.div`
  color: ${props => props.theme.textTertiary};
  font-size: 12px;
`

const EmptyNotification = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: ${props => props.theme.textSecondary};
  text-align: center;

  .icon {
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h4 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`

export default function MainLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [activePage, setActivePage] = useState("home")
  const { theme } = useTheme()
  const { isLoggedIn, user } = useAuth()
  
  // 모바일 메시지 패널 상태
  const [isMobileMessageOpen, setIsMobileMessageOpen] = useState(false)
  
  // 모바일 로그인 패널 상태
  const [isMobileLoginOpen, setIsMobileLoginOpen] = useState(false)
  
  // 헤더 알림 관련 상태
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [showNotificationPanel, setShowNotificationPanel] = useState(false)
  const [activityNotifications, setActivityNotifications] = useState([])


  // 로그인 모달 열기 이벤트 리스너
  useEffect(() => {
    const handleShowLoginModal = () => {
      setIsMobileLoginOpen(true);
    };

    window.addEventListener('showLoginModal', handleShowLoginModal);
    return () => window.removeEventListener('showLoginModal', handleShowLoginModal);
  }, []);

  // 현재 경로에 따라 활성 페이지 설정
  useEffect(() => {
    const path = location.pathname.split("/")[1]
    if (path === "home" || path === "") {
      setActivePage("home")
    } else {
      setActivePage(path)
    }
  }, [location])

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    setActivePage(page)
    navigate(page === "home" ? "/home" : `/${page}`)
  }

  // 모바일 메시지 패널 열기/닫기
  const toggleMobileMessage = () => {
    setIsMobileMessageOpen(!isMobileMessageOpen)
  }
  
  // 모바일 로그인 패널 열기/닫기
  const toggleMobileLogin = () => {
    setIsMobileLoginOpen(!isMobileLoginOpen)
  }
  


  // 읽지 않은 메시지 및 알림 개수 조회
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      if (isLoggedIn && user) {
        console.log('🔍 [알림 개수 조회 시작] user.id:', user.id);
        try {
          // 댓글 알림 개수
          const commentUrl = `/notification/unread-count/${user.id}?type=comment`;
          console.log('📡 [댓글 알림 요청] URL:', commentUrl);
          const commentResponse = await fetch(commentUrl, {
            credentials: 'include'
          });
          console.log('📥 [댓글 알림 응답] status:', commentResponse.status, 'ok:', commentResponse.ok);
          const commentData = await commentResponse.json();
          console.log('📦 [댓글 알림 데이터]', commentData);
          const commentCount = commentData.success ? commentData.count : 0;
          console.log('💬 읽지 않은 댓글 알림:', commentCount, '개')

          // 활동 알림 개수 (좋아요, 플레이)
          const activityUrl = `/notification/unread-count/${user.id}?type=activity`;
          console.log('📡 [활동 알림 요청] URL:', activityUrl);
          const activityResponse = await fetch(activityUrl, {
            credentials: 'include'
          });
          console.log('📥 [활동 알림 응답] status:', activityResponse.status, 'ok:', activityResponse.ok);
          const activityData = await activityResponse.json();
          console.log('📦 [활동 알림 데이터]', activityData);
          const activityCount = activityData.success ? activityData.count : 0;
          console.log('⭐ 읽지 않은 활동 알림:', activityCount, '개')

          // 모든 알림을 활동 알림으로 통합
          const totalCount = commentCount + activityCount;
          console.log('🔔 총 읽지 않은 알림:', totalCount, '개')
          setUnreadNotificationCount(totalCount);
        } catch (error) {
          console.error('❌ [알림 개수 조회 오류]:', error);
        }
      } else {
        console.log('⏭️ [알림 개수 조회 스킵] isLoggedIn:', isLoggedIn, 'user:', user);
      }
    };

    fetchUnreadCounts();

    // 30초마다 알림 개수 새로고침
    const interval = setInterval(fetchUnreadCounts, 30000);

    return () => clearInterval(interval);
  }, [isLoggedIn, user])

  const toggleNotificationPanel = async () => {
    const newState = !showNotificationPanel
    setShowNotificationPanel(newState)

    // 패널을 여는 경우 모든 알림 데이터 가져오기 (댓글 + 활동)
    if (newState && user) {
      try {
        // 댓글 알림 가져오기
        const commentResponse = await fetch(`/notification/list/${user.id}?type=comment`, {
          credentials: 'include'
        })
        const commentData = await commentResponse.json()
        const comments = commentData.success ? commentData.notifications || [] : []
        console.log('📬 댓글 알림:', comments.length, '개', comments)

        // 활동 알림 가져오기
        const activityResponse = await fetch(`/notification/list/${user.id}?type=activity`, {
          credentials: 'include'
        })
        const activityData = await activityResponse.json()
        const activities = activityData.success ? activityData.notifications || [] : []
        console.log('🎉 활동 알림:', activities.length, '개', activities)

        // 두 알림을 합쳐서 시간순으로 정렬
        const allNotifications = [...comments, ...activities].sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        )
        console.log('📋 통합 알림:', allNotifications.length, '개', allNotifications)

        setActivityNotifications(allNotifications)
      } catch (error) {
        console.error('활동 알림 조회 실패:', error)
      }
    }
  }

  const closeNotificationPanel = () => {
    setShowNotificationPanel(false)
  }

  const handleNotificationClick = async (notification) => {
    // 알림을 읽음 처리
    try {
      await fetch(`/notification/mark-read/${notification.notification_id}`, {
        method: 'PUT',
        credentials: 'include'
      })

      // 알림 카운트 업데이트 (댓글 + 활동 통합)
      setUnreadNotificationCount(prev => Math.max(0, prev - 1))
      // 목록에서 읽음 상태 업데이트
      setActivityNotifications(prev =>
        prev.map(n => n.notification_id === notification.notification_id
          ? { ...n, is_read: true }
          : n
        )
      )

      // 관련 페이지로 이동 (related_id가 있는 경우)
      if (notification.related_id) {
        console.log('알림 클릭:', notification.type, 'related_id:', notification.related_id)

        if (notification.type === 'comment') {
          // 댓글 알림 → 게시물 상세 페이지로 이동
          navigate(`/community/${notification.related_id}`)
        } else if (notification.type === 'post_like' || notification.type === 'like' || notification.type === 'comment_like') {
          // 커뮤니티 게시물/댓글 좋아요 알림 → 게시물 상세 페이지로 이동
          navigate(`/community/${notification.related_id}`)
        } else if (notification.type === 'quiz_like') {
          // 퀴즈 좋아요 알림 → 문제세트 상세 페이지로 이동
          navigate(`/problem/${notification.related_id}`)
        } else if (notification.type === 'play_milestone') {
          // 플레이 마일스톤 알림 → 문제세트 상세 페이지로 이동
          navigate(`/problem/${notification.related_id}`)
        } else {
          // 기타 알림은 일단 커뮤니티로 시도
          navigate(`/community/${notification.related_id}`)
        }
        closeNotificationPanel()
      }
    } catch (error) {
      console.error('알림 처리 실패:', error)
    }
  }

  const handleProfileClick = () => {
    navigate('/settings')
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return timestamp;

    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return "방금 전";
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;

    // 7일 이상 지난 경우 날짜 표시
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  const isCreateDetailPage = location.pathname === '/createdetail'

  return (
    <Container theme={theme}>
      <Navbar
        activePage={activePage}
        setActivePage={handlePageChange}
        onMobileMessageClick={toggleMobileMessage}
        onMobileLoginClick={toggleMobileLogin}
        onNotificationClick={toggleNotificationPanel}
        unreadNotificationCount={unreadNotificationCount}
      />

      <TopHeader theme={theme}>
        <HeaderSearch theme={theme}>
          <Search size={18} className="lead" />
          <input
            type="text"
            placeholder="문제 세트, 카테고리 검색..."
            onKeyDown={(e) => {
              const v = e.target.value.trim()
              if (e.key === 'Enter' && v) {
                navigate(`/home?q=${encodeURIComponent(v)}`)
              }
            }}
          />
          <span className="shortcut">/</span>
        </HeaderSearch>
        <HeaderActions>
          {isLoggedIn && (
            <WelcomeText theme={theme}>
              <span className="name">{user?.full_name || user?.username || '사용자'}</span>님 환영합니다
            </WelcomeText>
          )}
          {isLoggedIn && (
            <HeaderIconButton theme={theme} onClick={toggleNotificationPanel} title="알림">
              <Bell size={18} />
              {unreadNotificationCount > 0 && (
                <HeaderNotificationBadge theme={theme}>
                  {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                </HeaderNotificationBadge>
              )}
            </HeaderIconButton>
          )}
          {isLoggedIn && <ProfileDropdown />}
        </HeaderActions>
      </TopHeader>

      <MainWrapper style={isCreateDetailPage ? { paddingTop: 64, marginTop: 0, height: '100vh' } : { paddingTop: 64 }}>
        <Content className={activePage === "home" ? "home-layout" : ""} style={isCreateDetailPage ? { padding: 0, maxWidth: 'none', height: '100%' } : {}}>
          <MainContent className="mobile-main" style={isCreateDetailPage ? { height: '100%', overflow: 'hidden' } : {}}>
            <Outlet />
          </MainContent>
        </Content>
      </MainWrapper>
      
      

      {/* 모바일 메시지 패널 */}
      {isLoggedIn && (
        <MobileMessagePanel theme={theme} $isOpen={isMobileMessageOpen}>
          <MessagePanelHeader theme={theme}>
            <MessagePanelTitle theme={theme}>
              <MessageCircle size={20} />
              메시지
            </MessagePanelTitle>
            <CloseButton theme={theme} onClick={toggleMobileMessage}>
              <X size={20} />
            </CloseButton>
          </MessagePanelHeader>

          <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
            <EmptyMessage theme={theme}>
              <MessageCircle size={48} className="icon" />
              <h4>아직 메시지가 없습니다</h4>
              <p>새로운 대화를 시작해보세요!</p>
            </EmptyMessage>
          </div>
        </MobileMessagePanel>
      )}

      {/* 활동 알림 패널 (댓글 + 좋아요 + 플레이 통합) */}
      {isLoggedIn && (
        <NotificationPanel theme={theme} $isOpen={showNotificationPanel}>
          <NotificationHeader theme={theme}>
            <NotificationTitle theme={theme}>
              <Bell size={20} />
              활동 알림
            </NotificationTitle>
            <CloseButton theme={theme} onClick={closeNotificationPanel}>
              <X size={20} />
            </CloseButton>
          </NotificationHeader>

          <NotificationList>
            {activityNotifications.length > 0 ? (
              activityNotifications.map((notification) => {
                // 알림 타입에 따른 아이콘 선택
                const renderIcon = () => {
                  if (notification.type === 'play_milestone') {
                    return <Trophy size={20} />;
                  } else if (notification.type === 'like' || notification.type === 'post_like' || notification.type === 'comment_like') {
                    return <Heart size={20} />;
                  } else {
                    return <Bell size={20} />;
                  }
                };

                return (
                  <NotificationItem
                    key={notification.notification_id}
                    $isRead={notification.is_read}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <NotificationIcon theme={theme} $type={notification.type}>
                      {renderIcon()}
                    </NotificationIcon>
                    <NotificationContent>
                      <NotificationItemTitle>{notification.title}</NotificationItemTitle>
                      <NotificationItemMessage>{notification.message}</NotificationItemMessage>
                      <NotificationItemTime>{formatTime(notification.created_at)}</NotificationItemTime>
                    </NotificationContent>
                  </NotificationItem>
                );
              })
            ) : (
              <EmptyNotification theme={theme}>
                <Bell size={48} className="icon" />
                <h4>활동 알림이 없습니다</h4>
                <p>좋아요와 플레이 마일스톤을 달성하면 알림을 받을 수 있어요</p>
              </EmptyNotification>
            )}
          </NotificationList>
        </NotificationPanel>
      )}
    </Container>
  )
}

